import React from "react";
import { Box, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useAppDispatch } from "../../../../redux/useDispatch";
import BookingDatePicker from "../../common/components/OrderDataStep/BookingDatePicker";
// Time and duration selectors are not used for key handover host booking
import { useSelector } from "react-redux";
import { RootState } from "../../../../redux/store";
import moment from "moment-timezone";
// Helpers not needed in this step
import { quantity2Selected, quantitySelected } from "../../../../redux/slice/bookingCreation/bookingCreationSlice";
import LoaderOverlay from "../../../layout/components/LoaderOverlay";
import _ from "lodash";
// Quantity is captured via numeric input
import BookingQuantityInput from "../../common/components/OrderDataStep/BookingQuantityInput";
import {
  checkInDateSelected,
  checkOutDateSelected,
  setNextEnabled,
} from "../../../../redux/slice/bookingCreation/bookingCreationSlice";

const styles = {
  root: {
    padding: "2px",
    paddingTop: 0,
  },
  dateTimeWrapper: {
    width: "350px",
    display: "flex",
    flexDirection: "column",
    margin: `20px auto 0 auto`,
    "@media (min-width: 1280px)": {
      width: "350px",
    },
  },
  inputWrapper: {
    width: "350px",
    display: "flex",
    flexDirection: "column",
    margin: `12px auto 0 auto`,
    "@media (min-width: 1280px)": {
      width: "350px",
    },
  },
};

const OrderDataStep = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const groupAvailabilities = useSelector(
    (state: RootState) => state.bookingCreation.availabilities
  );
  const orderData = useSelector(
    (state: RootState) => state.bookingCreation.orderData
  );
  const selectedServiceType = useSelector(
    (state: RootState) => state.bookingCreation.keyHandoverSelectedService
  );

  const isLoading = _.isEmpty(groupAvailabilities);

  // no-op legacy handler removed

  const handleDropOffDateChange = (date: moment.Moment | null) => {
    dispatch(checkInDateSelected({ checkInDate: date }));
    // For pick-up only, default to 1 day window
    if (selectedServiceType === "Key-Pickup-Only" && date) {
      dispatch(
        checkOutDateSelected({ checkOutDate: date.clone().add(1, "day") })
      );
      dispatch(setNextEnabled(Boolean(orderData.quantity >= 1)));
    } else if (selectedServiceType === "Key-Pickup-And-Return") {
      // Always clear return date and wait for user selection; keep Next disabled until selected
      dispatch(checkOutDateSelected({ checkOutDate: null }));
      dispatch(setNextEnabled(false));
    }
  };

  const handleReturnDateChange = (date: moment.Moment | null) => {
    if (
      selectedServiceType === "Key-Pickup-And-Return" &&
      orderData.checkInDate &&
      orderData.quantity
    ) {
      dispatch(checkOutDateSelected({ checkOutDate: date }));
    } else if (date && orderData.checkInDate && date.isBefore(orderData.checkInDate)) {
      // Ensure return date is not before drop-off
      dispatch(
        checkOutDateSelected({ checkOutDate: orderData.checkInDate.clone() })
      );
    } else {
      dispatch(checkOutDateSelected({ checkOutDate: date }));
    }
    // Enable next when both dates are present
    const hasBoth = Boolean(orderData.checkInDate && date);
    if (selectedServiceType === "Key-Pickup-And-Return") {
      dispatch(setNextEnabled(hasBoth));
    }
  };

  let availableDates;
  if (groupAvailabilities) {
    availableDates = Object.keys(groupAvailabilities).filter((dateKey) => {
      const timeSlots = groupAvailabilities[dateKey];
      return Object.values(timeSlots).some((slot) =>
        // @ts-ignore
        Object.values(slot).some((value) => value > 0)
      );
    });
  }

  return (
    <Box sx={styles.root}>
      {groupAvailabilities &&
        (_.isEmpty(groupAvailabilities) ? (
          <Box className="pl-5">
            <Typography>
              {t("book_location.order_data.error.no_availabilities_for_group")}
            </Typography>
          </Box>
        ) : (
          <>
            {/* Render fields based on selected service type */}
            {selectedServiceType === "Key-Pickup-And-Return" ? (
              <>
                <Box sx={styles.dateTimeWrapper}>
                  {availableDates && (
                    <BookingDatePicker
                      value={orderData.checkInDate}
                      onChange={(date) => handleDropOffDateChange(date)}
                      shouldDisableDate={(date) => false}
                      availableDates={availableDates}
                      label={t(
                        "book_location.reservation_details.drop_off_date"
                      )}
                    />
                  )}
                </Box>
                <Box sx={styles.inputWrapper}>
                  <BookingQuantityInput
                    label={t(
                      "book_location.reservation_details.number_of_days"
                    )}
                    value={orderData.quantity}
                    onChange={(quantity) => {
                      dispatch(quantitySelected({ quantity }));
                      // For return flow, clear previously selected return date and wait for user to pick again
                      if (selectedServiceType === "Key-Pickup-And-Return") {
                        dispatch(checkOutDateSelected({ checkOutDate: null }));
                        dispatch(setNextEnabled(false));
                      }
                    }}
                    min={1}
                    step={1}
                    disabled={!orderData.checkInDate}
                  />
                </Box>
                <Box sx={styles.inputWrapper}>
                  {availableDates && (
                    <BookingDatePicker
                      value={orderData.checkOutDate}
                      onChange={(date) => handleReturnDateChange(date)}
                      shouldDisableDate={(date) => false}
                      availableDates={availableDates}
                      label={t("book_location.reservation_details.return_date")}
                      disabled={
                        selectedServiceType === "Key-Pickup-And-Return" &&
                        (!orderData.checkInDate || !orderData.quantity)
                      }
                      minDate={
                        orderData.checkInDate && orderData.quantity
                          ? orderData.checkInDate.clone().add(orderData.quantity, "day")
                          : null
                      }
                    />
                  )}
                </Box>
                <Box sx={styles.inputWrapper}>
                  <BookingQuantityInput
                    label={t(
                      "book_location.reservation_details.number_of_days"
                    )}
                    value={orderData.quantity2!}
                    onChange={(quantity) =>
                      dispatch(quantity2Selected({ quantity }))
                    }
                    min={1}
                    step={1}
                    disabled={!orderData.checkOutDate || !orderData.checkInDate}
                  />
                </Box>
              </>
            ) : (
              <>
                <Box sx={styles.dateTimeWrapper}>
                  {availableDates && (
                    <BookingDatePicker
                      value={orderData.checkInDate}
                      onChange={(date) => handleDropOffDateChange(date)}
                      shouldDisableDate={(date) => false}
                      availableDates={availableDates}
                      label={t(
                        "book_location.reservation_details.drop_off_date"
                      )}
                    />
                  )}
                </Box>
                <Box sx={styles.inputWrapper}>
                  <BookingQuantityInput
                    label={t(
                      "book_location.reservation_details.number_of_days"
                    )}
                    value={orderData.quantity}
                    onChange={(quantity) =>
                      dispatch(quantitySelected({ quantity }))
                    }
                    min={1}
                    step={1}
                    disabled={!orderData.checkInDate}
                  />
                </Box>
              </>
            )}
            <Box sx={styles.dateTimeWrapper}>
              <div className="bg-[#EDF0F5] rounded p-2 text-sm mb-2 w-full">
                <Typography>
                  <span className="font-semibold mr-1">
                    {t("book_location.reservation_details.nb")}:{" "}
                  </span>
                  {t("book_location.reservation_details.key_handover_note")}
                </Typography>
              </div>
            </Box>
          </>
        ))}
      <LoaderOverlay open={isLoading} />
    </Box>
  );
};

export default OrderDataStep;
